import PrimaryInnerGlowButton from '@/components/buttons/primary';
import InputField from '@/components/forms/InputField';
import PhoneField from '@/components/forms/PhoneField';
import SelectField from '@/components/forms/SelectField';
import Textarea from '@/components/forms/Textarea';
import {
  HubspotCompanySizeDropdownOptions,
  HubspotFieldLabels,
  HubspotFieldNames,
  HubspotFieldValidators,
  HubspotFormIDs,
  HubspotIndustryDropdownOptions,
  HubspotStartupFundingDropdownOptions,
  HubspotStartupReferralPartnerDropdownOptions,
} from '@/constants/hubspot';
import { StartupsPageQuery } from '@/graphql';
import StartupsPageQueryDocument from '@/graphql/queries/StartupsPage.gql';
import ModularPageLayout from '@/layouts/ModularPageLayout';
import { useCustomFormik, useFormikHubspotSubmit } from '@/lib/formik';
import { getStaticPageProps } from '@/lib/next';
import { parseHtml } from '@/lib/parse-html';
import { ModularPageProps, ModuleBuilderProps, parseModuleBuilderProps } from '@/modules/ModuleBuilder';
import { COLORS } from '@/styles/color';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

const StartupsPage: PageTemplate<ModularPageProps> = ({ modularPageProps }) => {
  const config = useMemo<Partial<ModuleBuilderProps['config']>>(
    () => ({
      formFooter: {
        children: ({ confirmationText }) => {
          /* eslint-disable react-hooks/rules-of-hooks */
          const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

          const formik = useCustomFormik({
            initialValues: {
              [HubspotFieldNames.Email]: '',
              [HubspotFieldNames.FirstName]: '',
              [HubspotFieldNames.LastName]: '',
              [HubspotFieldNames.JobTitle]: '',
              [HubspotFieldNames.Phone]: '',
              [HubspotFieldNames.CompanyName]: '',
              [HubspotFieldNames.Website]: '',
              [HubspotFieldNames.CompanySize]: '',
              [HubspotFieldNames.Industry]: '',
              [HubspotFieldNames.StartupFunding]: '',
              [HubspotFieldNames.StartupReferralPartner]: '',
              [HubspotFieldNames.StartupReferralPartnerFreeform]: '',
              [HubspotFieldNames.StartupReferralPartnerFundingURL]: '',
            },
            onSubmit: useFormikHubspotSubmit({
              formId: HubspotFormIDs.Startups,
              name: `Front for Startups Form`,
              content: `Front for Startups Form`,
              onSuccess: () => {
                setFormSubmitted(true);
              },
            }),
            validate: (values) => {
              const errors = {};

              errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
                values[HubspotFieldNames.Email],
                {
                  required: true,
                  allowPersonalEmail: true,
                  allowSharedEmail: true,
                },
              );

              errors[HubspotFieldNames.FirstName] = HubspotFieldValidators[HubspotFieldNames.FirstName](
                values[HubspotFieldNames.FirstName],
                {
                  required: true,
                },
              );

              errors[HubspotFieldNames.LastName] = HubspotFieldValidators[HubspotFieldNames.LastName](
                values[HubspotFieldNames.LastName],
                {
                  required: true,
                },
              );

              errors[HubspotFieldNames.JobTitle] = HubspotFieldValidators[HubspotFieldNames.JobTitle](
                values[HubspotFieldNames.JobTitle],
                {
                  required: true,
                },
              );

              errors[HubspotFieldNames.Phone] = HubspotFieldValidators[HubspotFieldNames.Phone](
                values[HubspotFieldNames.Phone],
                {
                  required: true,
                },
              );

              errors[HubspotFieldNames.CompanyName] = HubspotFieldValidators[HubspotFieldNames.CompanyName](
                values[HubspotFieldNames.CompanyName],
                {
                  required: true,
                },
              );

              errors[HubspotFieldNames.Website] = HubspotFieldValidators[HubspotFieldNames.Website](
                values[HubspotFieldNames.Website],
                {
                  required: true,
                },
              );

              errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
                values[HubspotFieldNames.CompanySize],
                {
                  required: true,
                },
              );

              errors[HubspotFieldNames.Industry] = HubspotFieldValidators[HubspotFieldNames.Industry](
                values[HubspotFieldNames.Industry],
                {
                  required: true,
                },
              );

              errors[HubspotFieldNames.StartupFunding] = HubspotFieldValidators[HubspotFieldNames.StartupFunding](
                values[HubspotFieldNames.StartupFunding],
                {
                  required: true,
                },
              );

              errors[HubspotFieldNames.StartupReferralPartner] = HubspotFieldValidators[
                HubspotFieldNames.StartupReferralPartner
              ](values[HubspotFieldNames.StartupReferralPartner], {
                required: true,
              });

              errors[HubspotFieldNames.StartupReferralPartnerFreeform] = HubspotFieldValidators[
                HubspotFieldNames.StartupReferralPartnerFreeform
              ](values[HubspotFieldNames.StartupReferralPartnerFreeform], {
                required:
                  values[HubspotFieldNames.StartupReferralPartner] ===
                  HubspotStartupReferralPartnerDropdownOptions['Other (Please provide)'],
              });

              errors[HubspotFieldNames.StartupReferralPartnerFundingURL] = HubspotFieldValidators[
                HubspotFieldNames.StartupReferralPartnerFundingURL
              ](values[HubspotFieldNames.StartupReferralPartnerFundingURL], {
                required: true,
              });

              if (Object.values(errors).every((val) => val === undefined)) return;

              return errors;
            },
          });

          if (formSubmitted) {
            return parseHtml(confirmationText);
          }

          return (
            <FormWrapper>
              <form onSubmit={formik.handleSubmit} noValidate>
                <InputField
                  type="email"
                  name={HubspotFieldNames.Email}
                  label={HubspotFieldLabels.Email}
                  placeholder="you@company.com"
                  value={formik.values[HubspotFieldNames.Email]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched[HubspotFieldNames.Email] && !!formik.errors[HubspotFieldNames.Email]}
                  errorMessage={formik.errors[HubspotFieldNames.Email] as string}
                />

                <FormFieldRow>
                  <InputField
                    type="text"
                    name={HubspotFieldNames.FirstName}
                    label={HubspotFieldLabels.FirstName}
                    placeholder="Jamie"
                    value={formik.values[HubspotFieldNames.FirstName]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched[HubspotFieldNames.FirstName] && !!formik.errors[HubspotFieldNames.FirstName]
                    }
                    errorMessage={formik.errors[HubspotFieldNames.FirstName] as string}
                  />

                  <InputField
                    type="text"
                    name={HubspotFieldNames.LastName}
                    label={HubspotFieldLabels.LastName}
                    placeholder="Smith"
                    value={formik.values[HubspotFieldNames.LastName]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched[HubspotFieldNames.LastName] && !!formik.errors[HubspotFieldNames.LastName]}
                    errorMessage={formik.errors[HubspotFieldNames.LastName] as string}
                  />
                </FormFieldRow>

                <FormFieldRow>
                  <InputField
                    type="text"
                    name={HubspotFieldNames.JobTitle}
                    label={HubspotFieldLabels.JobTitle}
                    placeholder="e.g., Director of Operations"
                    value={formik.values[HubspotFieldNames.JobTitle]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched[HubspotFieldNames.JobTitle] && !!formik.errors[HubspotFieldNames.JobTitle]}
                    errorMessage={formik.errors[HubspotFieldNames.JobTitle] as string}
                  />

                  <PhoneField
                    name={HubspotFieldNames.Phone}
                    label={HubspotFieldLabels.Phone}
                    value={formik.values[HubspotFieldNames.Phone]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched[HubspotFieldNames.Phone] && !!formik.errors[HubspotFieldNames.Phone]}
                    errorMessage={formik.errors[HubspotFieldNames.Phone] as string}
                  />
                </FormFieldRow>

                <FormFieldRow>
                  <InputField
                    type="text"
                    name={HubspotFieldNames.CompanyName}
                    label={HubspotFieldLabels.CompanyName}
                    placeholder="Your Company"
                    value={formik.values[HubspotFieldNames.CompanyName]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched[HubspotFieldNames.CompanyName] && !!formik.errors[HubspotFieldNames.CompanyName]
                    }
                    errorMessage={formik.errors[HubspotFieldNames.CompanyName] as string}
                  />

                  <InputField
                    type="text"
                    name={HubspotFieldNames.Website}
                    label={HubspotFieldLabels.Website}
                    placeholder="e.g. front.com"
                    value={formik.values[HubspotFieldNames.Website]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched[HubspotFieldNames.Website] && !!formik.errors[HubspotFieldNames.Website]}
                    errorMessage={formik.errors[HubspotFieldNames.Website] as string}
                  />
                </FormFieldRow>

                <FormFieldRow>
                  <SelectField
                    name={HubspotFieldNames.CompanySize}
                    label={HubspotFieldLabels.CompanySize}
                    value={formik.values[HubspotFieldNames.CompanySize]}
                    options={HubspotCompanySizeDropdownOptions}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched[HubspotFieldNames.CompanySize] && !!formik.errors[HubspotFieldNames.CompanySize]
                    }
                    errorMessage={formik.errors[HubspotFieldNames.CompanySize] as string}
                  />

                  <SelectField
                    name={HubspotFieldNames.Industry}
                    label={HubspotFieldLabels.Industry}
                    options={HubspotIndustryDropdownOptions}
                    value={formik.values[HubspotFieldNames.Industry]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched[HubspotFieldNames.Industry] && !!formik.errors[HubspotFieldNames.Industry]}
                    errorMessage={formik.errors[HubspotFieldNames.Industry] as string}
                    translucent
                  />
                </FormFieldRow>

                <FormFieldRow>
                  <SelectField
                    name={HubspotFieldNames.StartupFunding}
                    label={HubspotFieldLabels.StartupFunding}
                    options={HubspotStartupFundingDropdownOptions}
                    value={formik.values[HubspotFieldNames.StartupFunding]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched[HubspotFieldNames.StartupFunding] &&
                      !!formik.errors[HubspotFieldNames.StartupFunding]
                    }
                    errorMessage={formik.errors[HubspotFieldNames.StartupFunding] as string}
                    translucent
                  />

                  <SelectField
                    name={HubspotFieldNames.StartupReferralPartner}
                    label={HubspotFieldLabels.StartupReferralPartner}
                    options={HubspotStartupReferralPartnerDropdownOptions}
                    value={formik.values[HubspotFieldNames.StartupReferralPartner]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched[HubspotFieldNames.StartupReferralPartner] &&
                      !!formik.errors[HubspotFieldNames.StartupReferralPartner]
                    }
                    errorMessage={formik.errors[HubspotFieldNames.StartupReferralPartner] as string}
                    translucent
                  />
                </FormFieldRow>

                {formik.values[HubspotFieldNames.StartupReferralPartner] ===
                HubspotStartupReferralPartnerDropdownOptions['Other (Please provide)'] ? (
                  <Textarea
                    name={HubspotFieldNames.StartupReferralPartnerFreeform}
                    label={HubspotFieldLabels.StartupReferralPartnerFreeform}
                    value={formik.values[HubspotFieldNames.StartupReferralPartnerFreeform]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched[HubspotFieldNames.StartupReferralPartnerFreeform] &&
                      !!formik.errors[HubspotFieldNames.StartupReferralPartnerFreeform]
                    }
                    errorMessage={formik.errors[HubspotFieldNames.StartupReferralPartnerFreeform] as string}
                    rows={2}
                  />
                ) : null}

                <Textarea
                  name={HubspotFieldNames.StartupReferralPartnerFundingURL}
                  label={HubspotFieldLabels.StartupReferralPartnerFundingURL}
                  value={formik.values[HubspotFieldNames.StartupReferralPartnerFundingURL]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched[HubspotFieldNames.StartupReferralPartnerFundingURL] &&
                    !!formik.errors[HubspotFieldNames.StartupReferralPartnerFundingURL]
                  }
                  errorMessage={formik.errors[HubspotFieldNames.StartupReferralPartnerFundingURL] as string}
                  rows={2}
                />

                <PrimaryInnerGlowButton type="submit" disabled={formik.isValidating || formik.isSubmitting}>
                  {formik.isValidating || formik.isSubmitting ? 'Submitting' : 'Submit'}
                </PrimaryInnerGlowButton>
              </form>
            </FormWrapper>
          );
          /* eslint-enable react-hooks/rules-of-hooks */
        },
      },
    }),
    [modularPageProps],
  );

  return <ModularPageLayout props={modularPageProps} config={config} />;
};

StartupsPage.template = MasterTemplate;

export default StartupsPage;

export const getStaticProps = getStaticPageProps(
  (params) => `startups`,
  async ({ client }) => {
    const props = await client.request<StartupsPageQuery>(StartupsPageQueryDocument);

    if (!props.entry) {
      return { notFound: true };
    }

    return {
      props: parseModuleBuilderProps(props),
    };
  },
);

const FormWrapper = styled.div`
  position: relative;
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(30)};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${remCalc(20)};

  form {
    width: 100%;
    display: flex;
    flex-direction: column;
    row-gap: ${remCalc(20)};

    h3 {
      ${customTextSize(22, 28)};
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};

      &:not(:first-of-type) {
        margin-top: 1rem;
      }
    }

    ${PrimaryInnerGlowButton} {
      align-self: flex-start;
      z-index: 2;
    }
  }

  > p {
    text-align: center;

    a {
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
      text-decoration: underline;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    padding: ${remCalc(30)};
    align-items: center;
  }
`;

const FormFieldRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(20)};

  > div {
    flex-basis: 50%;
  }

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    column-gap: ${remCalc(20)};
  }
`;
